import { Component, OnDestroy, OnInit } from '@angular/core';
import { AuthService, FederalSubjectsService } from '@app/core/services';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { PARTNERS_FIELDS } from '@app/accounts/register-page/partners.fields';
import { VETERANS_FIELDS } from '@app/accounts/register-page/veterans.fields';
import { FieldDescription, Partner, User, UserCategory, Veteran, Citizenship } from '@app/entity';
import { flatten, head } from '@app/shared/utils';
import { combineLatest } from 'rxjs/observable/combineLatest';
import * as moment from 'moment';
import { Observable } from 'rxjs/Observable';
import { City } from '@app/entity/city';
import { FamilyRecord } from '@app/accounts/family-input/family-input.component';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators';
import { HttpEventType } from '@angular/common/http';

const PASSWORD_MIN_LEN = 8;

@Component({
  selector: 'app-auth-page',
  templateUrl: './register-page.component.html',
  styleUrls: ['./register-page.component.scss']
})
export class RegisterPageComponent implements OnInit, OnDestroy {
  section: 'veteran' | 'partner';
  pageType: 'register' | 'after-register';
  isPersonal: boolean; // if it is true then we can't change tabs

  form: FormGroup;
  fields: FieldDescription[] = [];
  isLoading = false;
  apiErrors = {};
  error: string = null;

  currentUser: User;

  maxDate: Date;
  birthDateValue: Date;
  categories: UserCategory[] = [];
  cities: Observable<City[]> = this.federalSubjecstService.cities;
  citizenships: Observable<Citizenship[]> = this.federalSubjecstService.getCitizenships();

  family: FamilyRecord[] = [];
  familyErrors = {};

  ngUnsubscribe = new Subject();

  photoLoader: any;

  isUploadingPhoto: boolean;
  hasPhoto: boolean;

  logout = () => this.authService.logout();

  constructor(
    private authService: AuthService,
    private router: Router,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private federalSubjecstService: FederalSubjectsService,
  ) {
  }

  ngOnInit() {
    this.maxDate = moment().subtract(18, 'year').toDate();

    combineLatest(this.route.queryParams, this.route.data)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([p, d]) => {
        this.section = p['section'] || 'veteran';
        this.pageType = d['section'];

        // this.fields = p['section'] === 'partner' ? PARTNERS_FIELDS : VETERANS_FIELDS;
        this.fields = VETERANS_FIELDS;

        this.isPersonal = p['tabs'] === 'hidden';
        this.authService.getCategories(this.section)
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe(categories => this.categories = categories);
        this.buildForm();
      });

    this.authService.user
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(user => {
        this.currentUser = user;
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private buildForm() {
    if (!this.fields) return;

    const controls = {};

    this.fields.forEach(field => controls[field.name] = [field.is_required ? null : '', ]);

    this.form = this.fb.group(controls);
  }

  submit() {
    if (this.isLoading) return;

    this.error = null;
    this.apiErrors = {};
    this.register();
  }

  private register() {
    const data = this.form.value;
    data['family'] = this.family;
    if (!this.validatePassword(data)) return;

    if (!data['agreement'] && this.section === 'partner') {
      this.apiErrors['agreement'] = 'Необходимо согласиться с публичной офертой для регистрации на портале';
      return;
    }

    this.isLoading = true;

    if (this.section === 'partner') this.prepareUserNames(data);

    this.getRegisterType(<User>data, data['password'])
      .pipe(takeUntil(this.ngUnsubscribe))
      .finally(() => this.isLoading = false)
      .subscribe(response => {
        if (this.section === 'partner') {
          this.photoLoader
            .subscribe(event => {
              if (event.type !== HttpEventType.Response) return;
              this.authService.updateProfile({ ...response, photo_id: event.body.id })
                .subscribe(success => this.router.navigateByUrl('/stocks/create'));
            });
        } else {
          // todo maybe add navigate to next?
          this.router.navigateByUrl('/');
        }
      }, error => this.checkError(error));
  }

  private getRegisterType(data: User, password: string) {
    return this.section === 'partner' ? this.authService.registerPartner(data, password) : this.authService.registerVeteran(data, password);
  }

  private checkError(error: any) {
    const json = error.error;
    if (error.status === 403) {
      this.error = json.detail;
      return;
    }
    if (error.status !== 400 || !json) {
      this.error = `Не удалось сохранить данные. Попробуйте еще раз. (${+error.status})`;
      return;
    }

    if (json['first_name'] || json['middle_name'] || json['last_name']) {
      json['fio'] = ['Введите 3 слова разделенные пробелами.'];
    }

    this.error = head(json['non_field_errors']);
    this.familyErrors = json['family'];
    this.apiErrors = flatten(json);
  }

  prepareUserNames(data) {
    if (!data['fio']) data['fio'] = '';

    const names = data['fio'].split(' ');

    data['last_name'] = !!names[0] ? names[0] : null;
    data['first_name'] = !!names[1] ? names[1] : null;
    data['middle_name'] = !!names[2] ? names[2] : null;
  }

  onChangeBirthDate() {
    this.form.patchValue({
      birth_date: !this.birthDateValue ? null : moment(this.birthDateValue).format('YYYY-MM-DD'),
    });
  }

  private validatePassword(data: any): boolean {
    if (!data['password'] || data['password'] !== data['re-password']) {
      this.apiErrors['password'] = 'Пароли не совпадают';
      this.apiErrors['re-password'] = 'Пароли не совпадают';
      return false;
    }
    if (data['password'].length < PASSWORD_MIN_LEN) {
      this.apiErrors['password'] = 'Длина пароля должна быть не менее 8 символов';
      return false;
    }
    return true;
  }

  insertPrefix(event) {
    event.currentTarget.value = '+7';
  }

  changeLoader(loader) {
    this.photoLoader = loader;
    this.isUploadingPhoto = false;
    this.hasPhoto = true;
  }
}



// WEBPACK FOOTER //
// ./src/app/accounts/register-page/register-page.component.ts